<template>
	<div class="printable-calendar">
		<header class="pc-header">
			<div class="pc-title">{{ titleText }}</div>
			<div class="pc-range">{{ rangeText }}</div>
		</header>

		<div v-if="loading" class="pc-muted">{{ loadingText }}</div>
		<div v-else-if="loadError" class="pc-muted">{{ loadError }}</div>

		<template v-else>
			<section v-if="mode === 'weeks'">
				<div class="pc-weekday-row">
					<div v-for="label in weekdayLabels" :key="label" class="pc-weekday-cell">{{ label }}</div>
				</div>
				<div class="pc-week-grid">
					<div v-for="day in weekGridDays" :key="day.key" class="pc-day-cell">
						<div class="pc-day-label" :class="day.outside ? 'pc-day-outside' : null">
							<span>{{ day.label }}</span>
							<span v-if="day.monthLabel">{{ day.monthLabel }}</span>
						</div>
						<div class="pc-events">
							<div v-for="evt in day.events" :key="evt.uid + '-' + day.key" class="pc-event">
								<span class="pc-event-time">{{ eventTimeLabel(evt, day.date) }}</span>
								<span class="pc-event-title">{{ evt.title }}</span>
								<span v-if="eventMeta(evt)" class="pc-event-meta"> · {{ eventMeta(evt) }}</span>
							</div>
						</div>
					</div>
				</div>
			</section>

			<section v-else-if="mode === 'days'">
				<div class="pc-section-title">{{ daysTitle }}</div>
				<div class="pc-days">
					<div v-for="day in dayCards" :key="day.key" class="pc-day-card">
						<div class="pc-day-card-title">{{ day.title }}</div>
						<div class="pc-events">
							<div v-for="evt in day.events" :key="evt.uid + '-' + day.key" class="pc-event">
								<span class="pc-event-time">{{ eventTimeLabel(evt, day.date) }}</span>
								<span class="pc-event-title">{{ evt.title }}</span>
								<span v-if="eventMeta(evt)" class="pc-event-meta"> · {{ eventMeta(evt) }}</span>
							</div>
							<div v-if="day.events.length === 0" class="pc-muted">{{ emptyText }}</div>
						</div>
					</div>
				</div>
			</section>

			<section v-else>
				<div class="pc-section-title">{{ agendaTitle }}</div>
				<div class="pc-list">
					<div v-for="day in agendaDays" :key="day.key" class="pc-list-day">
						<div class="pc-list-day-title">{{ day.title }}</div>
						<div class="pc-events">
							<div v-for="evt in day.events" :key="evt.uid + '-' + day.key" class="pc-event">
								<span class="pc-event-time">{{ eventTimeLabel(evt, day.date) }}</span>
								<span class="pc-event-title">{{ evt.title }}</span>
								<span v-if="eventMeta(evt)" class="pc-event-meta"> · {{ eventMeta(evt) }}</span>
							</div>
							<div v-if="day.events.length === 0" class="pc-muted">{{ emptyText }}</div>
						</div>
					</div>
				</div>
			</section>
		</template>
	</div>
</template>

<script setup lang="ts">
import {computed, onMounted, ref} from "vue";
import {injectEnv} from "@fd/Environment.ts";
import {useTodayStore} from "@/store/TodayStore.ts";
import {storeToRefs} from "pinia";
import {CalEvent, convertDiaryObjects, fetchCalendarData, normalizeRange, timeFormat} from "@/store/CalendarStore.ts";
import {weekInfo} from "@fd/editors/temporal/CalendarWidgetTypes.ts";
import {formatTimeOnly, sameDay, shiftDate} from "@t/util/DateUtil.ts";

type PrintMode = "weeks" | "days" | "list";

const props = withDefaults(defineProps<{
	mode: PrintMode;
	start: Date | string | number;
	end: Date | string | number;
	title?: string;
}>(), {
	mode: "weeks"
});

const env = injectEnv();
const today = useTodayStore();
const { type, id } = storeToRefs(today);

const loading = ref(true);
const loadError = ref<string | null>(null);
const events = ref<CalEvent[]>([]);

const displayStart = ref<Date | null>(null);
const displayEnd = ref<Date | null>(null);

const mode = computed<PrintMode>(() => props.mode);

const titleText = computed(() => {
	if (props.title && props.title.trim().length > 0) return props.title;
	return msgOr("menu.viewCalendar", "Calendar");
});

const rangeText = computed(() => {
	if (!displayStart.value || !displayEnd.value) return "";
	return rangeFormat.format(displayStart.value) + " – " + rangeFormat.format(displayEnd.value);
});

const weekdayLabels = computed(() => {
	const labels:string[] = [];
	const wi = weekInfo();
	const firstDay = wi.firstDay % 7;
	let d = new Date();
	while (d.getDay() !== firstDay) shiftDate(d, -1);
	for (let i = 0; i < 7; i++) {
		labels.push(weekdayFormat.format(d));
		shiftDate(d, 1);
	}
	return labels;
});

const weekGridDays = computed(() => {
	if (!displayStart.value || !displayEnd.value) return [];
	const wi = weekInfo();
	const startAligned = alignToWeekStart(displayStart.value, wi.firstDay);
	const endAligned = alignToWeekEnd(displayEnd.value, wi.firstDay);
	const days:WeekGridDay[] = [];
	let cursor = new Date(startAligned);
	while (cursor.getTime() <= endAligned.getTime()) {
		const day = new Date(cursor);
		const outside = day.getTime() < displayStart.value.getTime() || day.getTime() > displayEnd.value.getTime();
		const dayEvents = eventsForDay(day);
		days.push({
			key: dayKey(day),
			date: day,
			label: day.getDate().toString(),
			monthLabel: day.getDate() === 1 ? monthShortFormat.format(day) : "",
			outside,
			events: dayEvents
		});
		shiftDate(cursor, 1);
	}
	return days;
});

const dayCards = computed(() => {
	if (!displayStart.value || !displayEnd.value) return [];
	const days:DayCard[] = [];
	let cursor = new Date(displayStart.value);
	while (cursor.getTime() <= displayEnd.value.getTime()) {
		const day = new Date(cursor);
		days.push({
			key: dayKey(day),
			date: day,
			title: dayLongFormat.format(day),
			events: eventsForDay(day)
		});
		shiftDate(cursor, 1);
	}
	return days;
});

const agendaDays = computed(() => {
	if (!displayStart.value || !displayEnd.value) return [];
	return dayCards.value;
});

const daysTitle = computed(() => msgOr("calendar.view.days", "Days"));
const agendaTitle = computed(() => msgOr("calendar.view.list", "Agenda"));
const loadingText = computed(() => msgOr("being.loaded", "Loading"));
const emptyText = computed(() => msgOr("calendar.no.events", "No events"));

const weekdayFormat = new Intl.DateTimeFormat(undefined, { weekday: "short" });
const monthShortFormat = new Intl.DateTimeFormat(undefined, { month: "short" });
const dayLongFormat = new Intl.DateTimeFormat(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" });
const rangeFormat = new Intl.DateTimeFormat(undefined, { year: "numeric", month: "short", day: "numeric" });

onMounted(async () => {
	const start = toDate(props.start);
	const end = toDate(props.end);
	if (!start || !end) {
		loadError.value = msgOr("calendar.print.invalidRange", "Invalid date range");
		loading.value = false;
		return;
	}
	displayStart.value = normalizeDay(start);
	displayEnd.value = normalizeDay(end);
	const range = normalizeRange(displayStart.value, displayEnd.value);
	try {
		const cd = await fetchCalendarData(env, type.value, id.value, range.start, range.end);
		events.value = convertDiaryObjects(cd.events);
	} catch (err) {
		console.error(err);
		loadError.value = msgOr("calendar.print.loadError", "Unable to load calendar data");
	}
	loading.value = false;
});

function toDate(input: Date | string | number): Date | null {
	if (input instanceof Date) return new Date(input);
	if (typeof input === "number") return new Date(input);
	if (typeof input === "string") {
		const d = new Date(input);
		if (!isNaN(d.getTime())) return d;
	}
	return null;
}

function normalizeDay(d: Date): Date {
	const out = new Date(d);
	out.setHours(0, 0, 0, 0);
	return out;
}

function alignToWeekStart(d: Date, firstDay: number): Date {
	const target = firstDay % 7;
	const out = new Date(d);
	while (out.getDay() !== target) shiftDate(out, -1);
	return out;
}

function alignToWeekEnd(d: Date, firstDay: number): Date {
	const target = (firstDay + 6) % 7;
	const out = new Date(d);
	while (out.getDay() !== target) shiftDate(out, 1);
	return out;
}

function dayKey(d: Date): string {
	const year = d.getFullYear().toString();
	const month = pad2(d.getMonth() + 1);
	const day = pad2(d.getDate());
	return year + "-" + month + "-" + day;
}

function pad2(n: number): string {
	return n < 10 ? "0" + n : n.toString();
}

function eventsForDay(day: Date): CalEvent[] {
	const dayStart = normalizeDay(day);
	const dayEnd = new Date(dayStart);
	shiftDate(dayEnd, 1);
	const out:CalEvent[] = [];
	for (const e of events.value) {
		if (e.end.getTime() > dayStart.getTime() && e.start.getTime() < dayEnd.getTime()) {
			out.push(e);
		}
	}
	out.sort(compareEvents);
	return out;
}

function compareEvents(a: CalEvent, b: CalEvent): number {
	const aAllDay = !!a.allDay;
	const bAllDay = !!b.allDay;
	if (aAllDay && !bAllDay) return -1;
	if (!aAllDay && bAllDay) return 1;
	const startDiff = a.start.getTime() - b.start.getTime();
	if (startDiff !== 0) return startDiff;
	return a.end.getTime() - b.end.getTime();
}

function eventTimeLabel(e: CalEvent, day: Date): string {
	if (e.allDay) return msgOr("calendar.wholeDay", "All day");
	if (sameDay(e.start, e.end)) {
		return formatTimeOnly(e.start) + "–" + formatTimeOnly(e.end);
	}
	if (sameDay(e.start, day)) {
		return formatTimeOnly(e.start) + "–" + formatTimeOnly(e.end);
	}
	return timeFormat(e.start) + "–" + timeFormat(e.end);
}

function eventMeta(e: CalEvent): string {
	const parts: string[] = [];
	if (e.place) parts.push(e.place);
	if (e.partyName) parts.push(e.partyName);
	return joinParts(parts);
}

function joinParts(items: string[]): string {
	if (items.length === 0) return "";
	let out = items[0];
	for (let i = 1; i < items.length; i++) {
		out += " · " + items[i];
	}
	return out;
}

function msgOr(key: string, fallback: string): string {
	const value = env.msg(key);
	return value === key ? fallback : value;
}

interface WeekGridDay {
	key: string;
	date: Date;
	label: string;
	monthLabel: string;
	outside: boolean;
	events: CalEvent[];
}

interface DayCard {
	key: string;
	date: Date;
	title: string;
	events: CalEvent[];
}
</script>
